import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SeoJsonLd from '@/components/base/SeoJsonLd';

interface ArticleLayoutProps {
  title: string;
  subtitle: string;
  readTime: string;
  image: string;
  imageAlt: string;
  children: React.ReactNode;
}

export default function ArticleLayout({ title, subtitle, readTime, image, imageAlt, children }: ArticleLayoutProps) {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${title} | OOPBuy Spreadsheet Hub`;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', subtitle);
    }
  }, [location.pathname, title, subtitle]);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://oopbuyspreadsheets.lifestyle';
  const pageUrl = `${siteUrl}${location.pathname}`;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: subtitle,
    image: image,
    datePublished: '2026-06-21',
    dateModified: '2026-06-21',
    author: {
      '@type': 'Organization',
      name: 'OOPBuy Spreadsheet Hub',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'OOPBuy Spreadsheet Hub',
      url: siteUrl,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl,
    },
  };

  return (
    <>
      <SeoJsonLd data={articleJsonLd} />
      <article className="pt-24 pb-14 md:pt-28 md:pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <nav className="text-xs text-foreground-500 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
            <span className="mx-2">&rsaquo;</span>
            <span className="text-foreground-700">{title}</span>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-2.5 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">Guide</span>
              <span className="px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">OOPBuy Spreadsheet</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              {title}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              {subtitle}
            </p>
            <div className="flex items-center gap-3 mt-5 text-xs text-foreground-500">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-time-line text-sm"></i>
              </div>
              <span>{readTime}</span>
              <span>&middot;</span>
              <span>Updated June 2026</span>
            </div>
          </header>

          <div className="w-full h-56 md:h-72 rounded-lg overflow-hidden mb-10">
            <img
              src={image}
              alt={imageAlt}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="text-foreground-700 leading-relaxed">
            {children}
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">Continue Learning</h3>
                <p className="text-sm text-foreground-600">Explore more guides and resources on the OOPBuy Spreadsheet Hub.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Link
                  to="/"
                  className="px-5 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold rounded-md hover:bg-primary-600 transition-colors cursor-pointer text-center whitespace-nowrap"
                >
                  Back to Homepage
                </Link>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-5 py-2.5 bg-background-50 border border-background-200/70 text-foreground-700 text-sm font-semibold rounded-md hover:bg-background-100 transition-colors cursor-pointer text-center whitespace-nowrap"
                >
                  Visit Main Platform
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>
    </>
  );
}